package com.milta.feature.route.data.remote.dto

import com.milta.feature.route.domain.entity.DeliveryDraft
import com.milta.feature.route.domain.entity.OptimizationSkippedReason
import com.milta.feature.route.domain.entity.RouteBatchDispatchResult
import com.milta.feature.route.domain.entity.RouteBatchDriverProposal
import com.milta.feature.route.domain.entity.RouteBatchResult
import kotlinx.serialization.Serializable

/**
 * One pooled delivery to assign as part of a batch split — no driver/order here, the backend
 * decides both (MIL-39). [deliveryTimeLimit] is omitted (always null): it would need a full
 * timestamp, but [DeliveryDraft.deadlineTime] only carries a raw "HH:mm" with no date context —
 * same gap as `POST /routes` (DOM-02), which silently ignores the deadline on its request DTO too.
 * [packageCount] — retour 28/10: was never sent, so every delivery of a batch landed with 1 parcel
 * (backend `RouteBatchDeliveryItemDto.PackageCount`, null or < 1 stored as 1).
 */
@Serializable
data class RouteBatchDeliveryItemRequest(
    val recipientName: String,
    val address: String,
    val phoneNumber: String? = null,
    val latitude: Double? = null,
    val longitude: Double? = null,
    val packageCount: Int? = null,
    /** Retour 29/10 — HERE's match returned with [latitude]/[longitude] at the draft stage, kept by the back with them. */
    val resolvedAddress: String? = null,
)

fun DeliveryDraft.toBatchItemDto() = RouteBatchDeliveryItemRequest(
    recipientName = recipientName,
    address = address,
    phoneNumber = phoneNumber,
    latitude = latitude,
    longitude = longitude,
    packageCount = packageCount,
    resolvedAddress = resolvedAddress,
)

/** Request body of POST /route-batches (MIL-39). */
@Serializable
data class CreateRouteBatchRequest(
    val driverAccountIds: List<String>,
    val deliveries: List<RouteBatchDeliveryItemRequest>,
    val optimize: Boolean,
    // "yyyy-MM-dd" — mirrors CreateRouteRequest.date/CreateOwnRouteRequest.date: lets a manager
    // create a batch for tomorrow instead of always landing on server "today" (bug fix — the
    // dashboard's Today/Tomorrow tab was silently dropped when switching to the multi-driver
    // flow). Null/blank falls back to server "today".
    val date: String? = null,
    // MIL-99 — Enterprise's loading address, start point of every route of the batch (400
    // `loading_address_required` without it).
    val loadingAddressId: String? = null,
)

/** Mirrors the backend `RouteBatchDriverDto` — one driver's resulting route within the batch. */
@Serializable
data class RouteBatchDriverDto(
    val driverAccountId: String,
    val routeId: String,
    val deliveryCount: Int,
    val distanceKm: Double? = null,
    val durationMinutes: Int? = null,
    val deliveries: List<DeliveryGeocodeStatusResponseDto> = emptyList(),
    // MIL-99 — this driver's stops RoutaR couldn't place (address not located), at the end.
    val unlocatedDeliveryCount: Int = 0,
)

/** Mirrors the backend `RouteBatchResultDto` — shared shape for create and reoptimize (MIL-39). */
@Serializable
data class RouteBatchResultDto(
    val batchId: String,
    val totalDeliveries: Int,
    val totalDrivers: Int,
    val optimized: Boolean,
    val drivers: List<RouteBatchDriverDto>,
    // MIL-97 — "QuotaExceeded" when optimize=true was skipped, null otherwise.
    val optimizationSkippedReason: String? = null,
)

fun RouteBatchResultDto.toDomain() = RouteBatchResult(
    batchId = batchId,
    totalDeliveries = totalDeliveries,
    totalDrivers = totalDrivers,
    optimized = optimized,
    optimizationSkippedReason = OptimizationSkippedReason.fromApi(optimizationSkippedReason),
    drivers = drivers.map {
        RouteBatchDriverProposal(
            driverAccountId = it.driverAccountId,
            routeId = it.routeId,
            deliveryCount = it.deliveryCount,
            distanceKm = it.distanceKm,
            durationMinutes = it.durationMinutes,
            deliveries = it.deliveries.map { d -> d.toDomain() },
            unlocatedDeliveryCount = it.unlocatedDeliveryCount,
        )
    },
)

/** Mirrors the backend `DispatchRouteBatchResultDto` (MIL-39). */
@Serializable
data class DispatchRouteBatchResultDto(
    val batchId: String,
    val status: String,
    val dispatchedAt: String,
    val routeIds: List<String>,
)

fun DispatchRouteBatchResultDto.toDomain() = RouteBatchDispatchResult(
    batchId = batchId,
    status = status,
    dispatchedAt = dispatchedAt,
    routeIds = routeIds,
)
